"use client"

import { apiFetch } from "./api/client"
import type { AppNotification } from "./types"

const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY ?? ""

/** Browser capability check for web push. */
export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  )
}

/** True when running as an installed PWA (required for push on iOS). */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari legacy flag
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // iPadOS reports as Mac; detect touch to disambiguate
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  )
}

/**
 * True when the user is on iOS but NOT using Safari (Chrome iOS, Edge iOS,
 * Firefox iOS, Opera iOS, Yandex). These browsers wrap WebKit but don't
 * expose the Share → Add to Home Screen flow, so the install sheet shows
 * an "open in Safari" message instead of the step-by-step instructions.
 */
export function isNonSafariIOS(): boolean {
  if (!isIOS()) return false
  return /CriOS|EdgiOS|FxiOS|OPiOS|YaBrowser|EdgA/.test(navigator.userAgent)
}

/**
 * The active service worker registration, or null when none is registered.
 *
 * `navigator.serviceWorker.ready` NEVER settles if nothing has been registered
 * (dev, where components/service-worker.tsx registers production-only), so
 * awaiting it directly can hang a caller forever. `getRegistration()` resolves
 * immediately with undefined in that case.
 */
async function swRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!(await navigator.serviceWorker.getRegistration())) return null
  return navigator.serviceWorker.ready
}

/** Current granted+active subscription, or null. */
export async function getActiveSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null
  if (Notification.permission !== "granted") return null
  const reg = await swRegistration()
  if (!reg) return null
  return reg.pushManager.getSubscription()
}

/**
 * Request permission, subscribe, and register the subscription with Laravel.
 * Must be called from a user gesture (button click) — iOS requires it.
 * Returns true on success.
 */
export async function subscribeToPush(): Promise<boolean> {
  if (!isPushSupported()) return false

  const permission = await Notification.requestPermission()
  if (permission !== "granted") return false

  const reg = await swRegistration()
  if (!reg) return false
  let sub = await reg.pushManager.getSubscription()
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    })
  }

  // Laravel stores {endpoint, keys: {p256dh, auth}} per user and signs pushes
  // with the matching VAPID private key. apiFetch attaches the Sanctum bearer.
  // Best-effort: the browser subscription still exists locally even if the
  // server registration fails; the next subscribeToPush() call will retry.
  await apiFetch("/push/subscribe", {
    method: "POST",
    body: sub.toJSON(),
  }).catch(() => {})

  return true
}

/** Unsubscribe locally + tell Laravel to drop the subscription. */
export async function unsubscribeFromPush(): Promise<void> {
  if (!isPushSupported()) return
  const reg = await swRegistration()
  if (!reg) return
  const sub = await reg.pushManager.getSubscription()
  if (!sub) return

  await apiFetch("/push/subscribe", {
    method: "DELETE",
    body: { endpoint: sub.endpoint },
  }).catch(() => {})
  await sub.unsubscribe()
}

/** ---- notification feed (Laravel = source of truth) ---- */

export interface NotificationFeed {
  items: AppNotification[]
  unread: number
}

export async function fetchNotifications(): Promise<NotificationFeed> {
  const data = await apiFetch<NotificationFeed>("/notifications")
  return { items: data?.items ?? [], unread: data?.unread ?? 0 }
}

export async function apiMarkRead(id: string): Promise<void> {
  await apiFetch(`/notifications/${id}/read`, { method: "PATCH" })
}

export async function apiMarkAllRead(): Promise<void> {
  await apiFetch("/notifications/read-all", { method: "POST" })
}

/**
 * VAPID keys are URL-safe base64; the Push API needs a Uint8Array.
 * Passing the raw string silently fails to subscribe — this conversion is required.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
  const raw = window.atob(base64)
  const output = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i)
  return output
}
